import { RouteComponentProps } from "react-router-dom"
import { useProject } from "../../../projects/hooks/use-project"
import { useGetTasksQuery } from "../../../tasks/api"
import CircularProgress from "@material-ui/core/CircularProgress"
import TaskTreeGrid from "../../../treegrid/task-tree-grid/TaskTreeGrid"
import ErrorList from "../../../components/ErrorList"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import TaskListMobile from "../../../tasks/components/TaskListMobile"
import { useProjectCalendarSettings } from "../../providers/project-calendar-settings"
import { useTranslation } from "react-i18next"

type RouteParams = { projectId: string }

const queryOptions = { refetchOnMountOrArgChange: true } as const

const ProjectTasksRoute = ({ match }: RouteComponentProps<RouteParams>) => {
  const isSmallScreen = useIsSmallScreen()
  const translations = useTranslations()
  const projectId = match.params.projectId
  const {
    project,
    bulkAddTasks,
    isLoading: isLoadingProject,
    isError: isErrorProject,
  } = useProject(projectId, queryOptions)
  const tasksQuery = { project: projectId, archived: "false", links: "true", files: "true" } as const
  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    isError: isErrorTasks,
  } = useGetTasksQuery(tasksQuery, queryOptions)

  const { calendarSettings } = useProjectCalendarSettings()

  if (isLoadingProject || isLoadingTasks) return <CircularProgress />
  if (isErrorProject) return <ErrorList errors={[translations.fetchErrorProject]} />
  if (!project) return <ErrorList errors={[translations.projectNotFoundError]} />
  if (isErrorTasks) return <ErrorList errors={[translations.fetchErrorTasks]} />

  const { canCreateProjectTasks } = project

  return isSmallScreen ? (
    <TaskListMobile rootTaskId={null} tasks={tasks} canAddTasks={canCreateProjectTasks} onAddTask={bulkAddTasks} />
  ) : (
    <TaskTreeGrid
      key={projectId}
      project={project}
      tasks={tasks}
      calendarSettings={calendarSettings}
      onAddTask={bulkAddTasks}
    />
  )
}

const defaultTranslations = {
  projectNotFoundError: "Project not found",
  fetchErrorProject: "Failed to fetch project",
  fetchErrorTasks: "Failed to fetch tasks",
  fetchErrorHolidays: "Failed to fetch holidays",
}

const useTranslations = (): typeof defaultTranslations => {
  const { t } = useTranslation("translation")

  return {
    projectNotFoundError: t("projectNotFoundError", defaultTranslations.projectNotFoundError),
    fetchErrorProject: t("fetchErrorProject", defaultTranslations.fetchErrorProject),
    fetchErrorTasks: t("fetchErrorTasks", defaultTranslations.fetchErrorTasks),
    fetchErrorHolidays: t("fetchErrorHolidays", defaultTranslations.fetchErrorHolidays),
  }
}

export default ProjectTasksRoute
